import React, { useRef, useEffect, useState } from 'react'
import { getRequest, pathname } from '@helpers/javascript/javascript'
import { useCalendar } from './useCalendar'

const detailsCache    = new Map()
let   cacheGeneration = 0   // a reply asked for before a reload is not cached
const HOVER_DELAY     = 150 // ms: sweeping the mouse across cards fetches nothing

const SmallCard = () => {
  const { state, TOOLS } = useCalendar()
  const cardRef = useRef(null)
  const [position, setPosition] = useState(null)
  const [details,  setDetails]  = useState(null)

  const hovered = state.showSmallCard
  const info    = hovered && (hovered.externalCard
    ? hovered.externalCard.info
    : hovered.event?._def?.extendedProps?.info)
  // Bookings keep their own short card: no workorder details.
  const detailsId = TOOLS.showPopovers && info && !info.is_booking ? info.workorder_id : null

  useEffect(() => {
    detailsCache.clear()
    cacheGeneration += 1
  }, [state.events, state.openEvents])

  useEffect(() => {
    setDetails(detailsCache.get(detailsId) || null)
    if (!detailsId || detailsCache.has(detailsId)) return

    let cancelled    = false
    const generation = cacheGeneration
    const timer      = setTimeout(() => {
      getRequest(pathname({ path: '/calendar/event_details', params: { workorder_id: detailsId } }))
        .then(res => {
          // An expired session answers 200 with a login page, not the details.
          if (res?.workorder_id !== detailsId) throw new Error('Unexpected event details')
          if (generation === cacheGeneration) detailsCache.set(detailsId, res)
          if (!cancelled) setDetails(res)
        })
        .catch(() => { if (!cancelled) setDetails({ workorder_id: detailsId }) })
    }, HOVER_DELAY)

    return () => { cancelled = true; clearTimeout(timer) }
  }, [detailsId])

  useEffect(() => {
    if (!state.showSmallCard || !cardRef.current) {
      setPosition(null)
      return
    }

    const updatePosition = () => {
      const rect = cardRef.current.getBoundingClientRect()
      const padding = 10
      const positionData = state.showSmallCard.externalCard
        ? state.showSmallCard.externalCard.position
        : state.showSmallCard.jsEvent

      const { clientX, clientY } = positionData

      let finalX = clientX + 5
      let finalY = clientY + 5

      if (finalX + rect.width > window.innerWidth - padding) {finalX = clientX - rect.width - 5}
      if (finalX < padding) {finalX = padding}
      if (finalY + rect.height > window.innerHeight - padding) {finalY = clientY - rect.height - 5}
      if (finalY < padding) {finalY = padding}

      setPosition({ x: finalX, y: finalY })
    }

    updatePosition()
  }, [state.showSmallCard, details])

  if (!TOOLS.showPopovers || !state.showSmallCard) return null

  // Background events (holidays, absences) carry no workorder info: no popover.
  if (!info) return null
  if (state.showSmallCard.event?._def?.extendedProps?.absence_id) return null

  const loading   = detailsId && details?.workorder_id !== detailsId
  const smallCard = loading ? info : { ...info, ...details }

  const positionData = state.showSmallCard.externalCard
    ? state.showSmallCard.externalCard.position
    : state.showSmallCard.jsEvent

  const { clientX, clientY } = positionData

  return (
    <div
      ref={cardRef}
      className='new-tooltip wo-tooltip wo-tooltip--structured'
      style={{
        top:        position ? `${position.y}px` : `${clientY + 5}px`,
        left:       position ? `${position.x}px` : `${clientX + 5}px`,
        visibility: position ? 'visible'         : 'hidden'
      }}
    >
      <div className='wo-tooltip__header'>
        <div className='wo-tooltip__topline'>
          {smallCard.code && <span className='wo-tooltip__code'>{smallCard.code}</span>}
          {smallCard.status_badge && (() => {
            const [cls, label] = smallCard.status_badge
            return <span className={`wo-status-badge ${cls}`}>{label}</span>
          })()}
        </div>
        <div className='wo-tooltip__titlerow'>
          <p className='wo-tooltip__title'>{smallCard.title}</p>
          <div className='wo-tooltip__time'>
            <span className='strong'>
              {smallCard.event_start}{smallCard.event_end && `–${smallCard.event_end}`}
            </span>
            {smallCard.duration &&
              <span className='muted'><i className='fas fa-hourglass-half'></i>{smallCard.duration}</span>}
          </div>
        </div>
      </div>

      <div className='wo-tooltip__body'>
        {(smallCard.category_name || (Array.isArray(smallCard.field_badges) && smallCard.field_badges.length > 0)) &&
          <div className='wo-tooltip__badges'>
            {smallCard.category_name &&
              <span className='wo-tooltip__pill'
                style={{ background: `${smallCard.category_color}1a`, color: smallCard.category_color }}>
                {smallCard.category_name}
              </span>}
            {Array.isArray(smallCard.field_badges) && smallCard.field_badges.map((badge, i) => (
              <span key={i} className='wo-tooltip__pill'
                style={{ background: badge.bg_color, color: badge.text_color }}>
                {badge.label}
              </span>
            ))}
          </div>}
        {loading && <p className='wo-tooltip__desc muted'><i className='fas fa-spinner fa-spin'></i></p>}
        {smallCard.description && <p className='wo-tooltip__desc'>{smallCard.description}</p>}
        <div className='wo-tooltip__list'>
          {smallCard.travel_time && <><i className='fas fa-route'></i><span>{smallCard.travel_time}</span></>}
          {(smallCard.material_placements || []).map((action, i) => (
            <React.Fragment key={`material-placement-${i}`}>
              <i className={`fas ${action.action === 'pickup' ? 'fa-arrow-up' : 'fa-arrow-down'}`}></i><span>{action.label}</span>
            </React.Fragment>
          ))}
          {smallCard.travel_detail && <><i className='fas fa-tachometer-alt'></i><span>{smallCard.travel_detail}</span></>}
          {smallCard.partner    && <><i className='fas fa-handshake'></i><span>{smallCard.partner}</span></>}
          {smallCard.client     && <><i className='fas fa-user-circle'></i><span>{smallCard.client}</span></>}
          {smallCard.site       && <><i className='fas fa-building'></i><span>{smallCard.site}</span></>}
          {smallCard.address    && <><i className='fas fa-location-arrow'></i><span>{smallCard.address}</span></>}
        </div>
      </div>

      {smallCard.technician &&
        <div className='wo-tooltip__footer'>
          <span className='technician-dot' style={{ background: smallCard.technician_color }}></span>
          <b>{smallCard.technician}</b>
        </div>}
    </div>
  )
}

export default SmallCard
